import { Box, Divider, Group, ScrollArea, Stack, Text, Tooltip } from '@mantine/core'
import { QuestionIcon } from '@phosphor-icons/react'
import type { ReactNode } from 'react'
import type {
  BogglePlaygroundBonusPreview,
  BoggleResolvedChallengeRun,
} from '~/boggle/challenges'
import { getBoggleBonusRewardHumanReadable } from '~/boggle/config/bonuses/bonusValue'
import {
  BOGGLE_CHALLENGE_PATH_MODE_DESCRIPTIONS,
  BOGGLE_CHALLENGE_PATH_MODE_LABELS,
} from '~/boggle/config/challengePathMode'
import type {
  BoggleChallengeVowelRatioBand,
  BoggleResolvedChallengeBoardDistribution,
  BoggleResolvedChallengeBonus,
} from '~/boggle/config/challenges/modTypes'
import type { LetterWeight } from '~/boggle/engine/core'
import { IconGlyph } from '~/boggle/ui/atoms/Icons'
import { ChallengeSpecialTilesSection } from '~/boggle/ui/challenge/ChallengeSpecialTilesSection'
import { LetterDistribution } from '~/boggle/ui/challenge/LetterDistribution'

const BOARD_DISTRIBUTION_INLINE_HEIGHT_PX = 60

export function BoardDistributionLine({
  boardDistribution,
  letterWeights,
  vowelRatioBand,
}: {
  boardDistribution: BoggleResolvedChallengeBoardDistribution | null
  letterWeights: ReadonlyArray<LetterWeight>
  vowelRatioBand?: BoggleChallengeVowelRatioBand | null
}) {
  if (!boardDistribution) {
    return null
  }

  return (
    <Box mah={BOARD_DISTRIBUTION_INLINE_HEIGHT_PX} miw={0} w="100%">
      <LetterDistribution
        chartHeightPx={BOARD_DISTRIBUTION_INLINE_HEIGHT_PX}
        letterWeights={letterWeights}
        vowelRatioBand={vowelRatioBand}
      />
    </Box>
  )
}

export function ChallengeBonusPreview({
  bonuses,
}: {
  bonuses: readonly BoggleResolvedChallengeBonus[]
}) {
  if (bonuses.length === 0) {
    return null
  }

  return (
    <Stack align="center" bdrs={6} gap={8} miw={260} pb={10} pt={4} px={12}>
      <Divider color="dark.5" label="Bonuses" labelPosition="center" w={200} mt={30} mb={10} />
      <Stack gap={10} w="100%">
        {bonuses.map(bonus => (
          <Stack
            align="center"
            bd="1px solid #a6a16c"
            bg="#4b492e"
            bdrs={6}
            gap={4}
            key={bonus.id}
            pb={6}
            pt={8}
            px={12}
            w="100%"
          >
            <Text fz={14} fw={400} lh={1.35} ta="center" ff="Tilt Neon, sans-serif">
              {bonus.description}
            </Text>
            <Text
              bdrs={20}
              c="yellow.1"
              fz={12}
              fw={500}
              lh={1.3}
              ta="center"
              tt="capitalize"
            >
              {getBoggleBonusRewardHumanReadable(bonus.value)}
            </Text>
          </Stack>
        ))}
      </Stack>
    </Stack>
  )
}

function ChallengeStagingColumn({
  label,
  middle,
  footer,
  flex,
  justify = 'space-between',
  maw = 80,
  miw = 0,
  pillarHeight = 80,
}: {
  label: ReactNode
  middle: ReactNode
  footer?: ReactNode
  flex?: boolean
  justify?: 'flex-start' | 'space-between'
  maw?: number | string
  miw?: number
  pillarHeight?: null | number
}) {
  return (
    <Stack
      align="center"
      gap={4}
      h={pillarHeight === null ? undefined : pillarHeight}
      justify={justify}
      maw={maw}
      miw={miw}
      px={12}
      py={14}
      style={flex ? { flex: 1 } : undefined}
    >
      <Text c="#caa46a" fz={12} lts="0.12em" tt="uppercase">
        {label}
      </Text>
      <Text c="#f2e8d9" fz={22} fw={700} lh={1} ta="left" mt={4}>
        {middle}
      </Text>
      {footer}
    </Stack>
  )
}

function ChallengeStagingRow({ label, middle }: { label: ReactNode; middle: ReactNode }) {
  return (
    <Group justify="center" align="baseline" gap={12} w="100%">
      <Text c="#caa46a" fz={12} tt="capitalize">
        {label}
      </Text>
      <Box mt={4}>{middle}</Box>
    </Group>
  )
}

export function ChallengeStagingDetails({
  resolvedRun,
}: {
  resolvedRun: BoggleResolvedChallengeRun | BogglePlaygroundBonusPreview
}) {
  const coreColumns = resolvedRun.cores.filter(core => !core.isRow)
  const coreRows = resolvedRun.cores.filter(core => core.isRow)

  return (
    <ScrollArea flex={1} maw={540} w="100%">
      <Stack bdrs={8} gap={16} mih="100%" p={20} align="center" px={30} w="100%">
        <Stack gap={20}>
          <Group align="center" justify="center" gap={40} grow wrap="nowrap" w="100%">
            {coreColumns.map(core => (
              <ChallengeStagingColumn
                flex
                footer={
                  <Text
                    bg="rgba(255, 255, 255, 0.12)"
                    bdrs={4}
                    c="yellow.1"
                    fw={400}
                    fz={10}
                    opacity={0.6}
                    pb={3}
                    pt={2}
                    px={5}
                    ta="center"
                  >
                    {core.tierLabel}
                  </Text>
                }
                key={core.id}
                label={core.label}
                middle={core.visual}
              />
            ))}
          </Group>

          {coreRows.map(core => (
            <ChallengeStagingRow key={core.id} label={core.label} middle={core.visual} />
          ))}

          {resolvedRun.pathMode !== 'free' ? (
            <Group align="center" justify="center" gap={0} w="100%">
              <Tooltip
                bg="dark.8"
                events={{ hover: true, focus: true, touch: true }}
                label={BOGGLE_CHALLENGE_PATH_MODE_DESCRIPTIONS[resolvedRun.pathMode]}
                position="bottom"
                withArrow={false}
                withinPortal={false}
              >
                <Group>
                  <ChallengeStagingColumn
                    justify="flex-start"
                    label="Path"
                    maw="100%"
                    middle={
                      <Group pos="relative">
                        <Text fz={15} fw={700}>
                          {BOGGLE_CHALLENGE_PATH_MODE_LABELS[resolvedRun.pathMode]}
                        </Text>
                        <Box
                          c="rgba(255, 255, 255, 0.5)"
                          opacity={0.5}
                          pos="absolute"
                          right={-20}
                          top="50%"
                          style={{ transform: 'translateY(-50%)' }}
                        >
                          <IconGlyph
                            color="white"
                            icon={QuestionIcon}
                            size={14}
                            weight="fill"
                          />
                        </Box>
                      </Group>
                    }
                    pillarHeight={null}
                  />
                </Group>
              </Tooltip>
            </Group>
          ) : null}

          <ChallengeBonusPreview bonuses={resolvedRun.bonuses} />

          <ChallengeSpecialTilesSection entries={resolvedRun.specialTileReveals} />
        </Stack>
        <BoardDistributionLine
          boardDistribution={resolvedRun.boardDistribution}
          letterWeights={resolvedRun.gameplay.letterWeights}
          vowelRatioBand={resolvedRun.gameplay.vowelRatioBand}
        />
      </Stack>
    </ScrollArea>
  )
}
